import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Play, AlertCircle, Loader, Film, Maximize, Minimize, Settings, RefreshCw, Captions, CaptionsOff, BookOpen, ChevronDown, ChevronUp } from 'lucide-react';
import { getPlaybackUrl } from '../../../services/muxVideoService';

const MAX_RETRIES = 3;

const formatTime = (seconds) => {
  if (!seconds || isNaN(seconds)) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s?.toString()?.padStart(2, '0')}`;
};

/**
 * Find the active caption segment for the current playback time.
 */
const getActiveCaption = (captions, currentTime) => {
  if (!captions?.length) return null;
  // Find the last segment whose timestamp_seconds <= currentTime
  let active = null;
  for (let i = 0; i < captions?.length; i++) {
    const seg = captions?.[i];
    const start = seg?.timestamp_seconds ?? 0;
    const end = seg?.end_seconds ?? (captions?.[i + 1]?.timestamp_seconds ?? Infinity);
    if (currentTime >= start && currentTime < end) {
      active = seg;
      break;
    }
  }
  return active;
};

const MuxVideoPlayer = ({ videoId, muxPlaybackId, title, thumbnailUrl: propThumbnail, className = '', onVideoComplete, onWatchEvent, captions = [] }) => {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const containerRef = useRef(null);
  const retryCountRef = useRef(0);
  const retryTimerRef = useRef(null);
  const progressBarRef = useRef(null);
  const isDraggingRef = useRef(false);
  const firedMilestonesRef = useRef(new Set());
  const replayCountRef = useRef(0);

  const [playbackData, setPlaybackData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fatalError, setFatalError] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [buffering, setBuffering] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [qualityLevels, setQualityLevels] = useState([]);
  const [currentLevel, setCurrentLevel] = useState(-1);
  const [showQualityMenu, setShowQualityMenu] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [watchPercent, setWatchPercent] = useState(0);

  // Caption state
  const [captionsEnabled, setCaptionsEnabled] = useState(true);
  const [showTeachingPanel, setShowTeachingPanel] = useState(false);

  const keyPoints = captions?.filter(c => c?.is_key_point) || [];
  const activeCaptionSeg = playing ? getActiveCaption(captions, currentTime) : null;

  useEffect(() => {
    if (muxPlaybackId) {
      // Direct Mux playback ID provided — skip edge function lookup
      const hlsUrl = `https://stream.mux.com/${muxPlaybackId}.m3u8`;
      const thumbnailUrl = `https://image.mux.com/${muxPlaybackId}/thumbnail.jpg?time=0`;
      setPlaybackData({ hlsUrl, thumbnailUrl, playbackId: muxPlaybackId });
      setLoading(false);
      setError(null);
      setFatalError(false);
      return () => {
        hlsRef?.current?.destroy();
        clearTimeout(retryTimerRef?.current);
      };
    }
    if (!videoId) return;
    loadPlaybackUrl();
    return () => {
      hlsRef?.current?.destroy();
      clearTimeout(retryTimerRef?.current);
    };
  }, [videoId, muxPlaybackId]);

  // Fullscreen change listener
  useEffect(() => {
    const handleFullscreenChange = () => {
      const fsEl =
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement;
      setIsFullscreen(!!fsEl);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, []);

  const loadPlaybackUrl = async () => {
    setLoading(true);
    setError(null);
    setFatalError(false);
    try {
      const data = await getPlaybackUrl(videoId);
      setPlaybackData(data);
    } catch (err) {
      setError(err?.message || 'Failed to load video');
    } finally {
      setLoading(false);
    }
  };

  const scheduleRetry = useCallback((hlsUrl) => {
    if (retryCountRef?.current >= MAX_RETRIES) {
      setFatalError(true);
      setError('Video playback failed after multiple attempts.');
      return;
    }
    const delay = Math.pow(2, retryCountRef?.current) * 1000;
    retryCountRef.current += 1;
    retryTimerRef.current = setTimeout(() => {
      initHls(hlsUrl);
    }, delay);
  }, []);

  const initHls = useCallback(async (hlsUrl) => {
    const videoEl = videoRef?.current;
    if (!videoEl) return;

    hlsRef?.current?.destroy();
    setBuffering(false);

    if (videoEl?.canPlayType('application/vnd.apple.mpegurl')) {
      videoEl.src = hlsUrl;
      // Wait for the browser to load enough data before calling play()
      // to avoid NotSupportedError when play() is called too early
      const onCanPlay = () => {
        videoEl?.removeEventListener('canplay', onCanPlay);
        videoEl?.play()?.catch(err => {
          if (err?.name !== 'AbortError') console.error(err);
        });
      };
      videoEl?.addEventListener('canplay', onCanPlay);
    } else {
      try {
        const Hls = (await import('hls.js'))?.default;
        if (Hls?.isSupported()) {
          const hls = new Hls({
            enableWorker: true,
            lowLatencyMode: false,
          });
          hlsRef.current = hls;
          hls?.loadSource(hlsUrl);
          hls?.attachMedia(videoEl);

          hls?.on(Hls?.Events?.MANIFEST_PARSED, (event, data) => {
            const levels = hls?.levels?.map((lvl, idx) => ({
              index: idx,
              label: lvl?.height ? `${lvl?.height}p` : `Level ${idx + 1}`,
            })) || [];
            setQualityLevels(levels);
            setCurrentLevel(-1);
            videoEl?.play()?.catch(console.error);
          });

          hls?.on(Hls?.Events?.LEVEL_SWITCHED, (event, data) => {
            if (hls?.autoLevelEnabled) {
              setCurrentLevel(-1);
            }
          });

          hls?.on(Hls?.Events?.BUFFER_STALLED, () => {
            setBuffering(true);
          });

          hls?.on(Hls?.Events?.BUFFER_FLUSHED, () => {
            setBuffering(false);
          });

          hls?.on(Hls?.Events?.ERROR, (event, data) => {
            if (data?.fatal) {
              console.error('HLS fatal error:', data);
              setError('Stream error. Retrying...');
              scheduleRetry(hlsUrl);
            }
          });
        } else {
          setError('HLS playback is not supported in this browser.');
          setFatalError(true);
        }
      } catch (err) {
        console.error('Failed to load hls.js:', err);
        setError('Failed to initialize video player.');
        setFatalError(true);
      }
    }
  }, [scheduleRetry]);

  const handlePlay = () => {
    if (!playbackData?.hlsUrl) return;
    retryCountRef.current = 0;
    setError(null);
    setFatalError(false);
    if (playing || watchPercent > 0) {
      replayCountRef.current += 1;
    }
    firedMilestonesRef.current = new Set();
    setPlaying(true);
    initHls(playbackData?.hlsUrl);
  };

  const handleManualRetry = () => {
    retryCountRef.current = 0;
    setError(null);
    setFatalError(false);
    if (playing && playbackData?.hlsUrl) {
      initHls(playbackData?.hlsUrl);
    } else {
      loadPlaybackUrl();
    }
  };

  const handleQualityChange = (levelIndex) => {
    const hls = hlsRef?.current;
    if (!hls) return;
    hls.currentLevel = levelIndex;
    setCurrentLevel(levelIndex);
    setShowQualityMenu(false);
  };

  const toggleFullscreen = () => {
    const el = containerRef?.current;
    if (!el) return;
    if (!isFullscreen) {
      if (el?.requestFullscreen) el?.requestFullscreen();
      else if (el?.webkitRequestFullscreen) el?.webkitRequestFullscreen();
      else if (el?.mozRequestFullScreen) el?.mozRequestFullScreen();
      else if (el?.msRequestFullscreen) el?.msRequestFullscreen();
    } else {
      if (document?.exitFullscreen) document.exitFullscreen();
      else if (document?.webkitExitFullscreen) document.webkitExitFullscreen();
      else if (document?.mozCancelFullScreen) document.mozCancelFullScreen();
      else if (document?.msExitFullscreen) document.msExitFullscreen();
    }
  };

  const seekToTimestamp = (seconds) => {
    const videoEl = videoRef?.current;
    if (!videoEl) return;
    videoEl.currentTime = seconds;
    if (!playing) {
      handlePlay();
    }
  };

  const scrubToPosition = useCallback((clientX) => {
    const bar = progressBarRef?.current;
    const videoEl = videoRef?.current;
    if (!bar || !videoEl || !duration) return;
    const rect = bar?.getBoundingClientRect();
    const ratio = Math.min(Math.max((clientX - rect?.left) / rect?.width, 0), 1);
    videoEl.currentTime = ratio * duration;
  }, [duration]);

  const handleProgressMouseDown = (e) => {
    isDraggingRef.current = true;
    scrubToPosition(e?.clientX);
    const onMove = (ev) => { if (isDraggingRef?.current) scrubToPosition(ev?.clientX); };
    const onUp = () => { isDraggingRef.current = false; window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const handleProgressTouchStart = (e) => {
    isDraggingRef.current = true;
    scrubToPosition(e?.touches?.[0]?.clientX);
    const onMove = (ev) => { if (isDraggingRef?.current) scrubToPosition(ev?.touches?.[0]?.clientX); };
    const onEnd = () => { isDraggingRef.current = false; window.removeEventListener('touchmove', onMove); window.removeEventListener('touchend', onEnd); };
    window.addEventListener('touchmove', onMove);
    window.addEventListener('touchend', onEnd);
  };

  const handleTimeUpdate = () => {
    const videoEl = videoRef?.current;
    if (!videoEl) return;
    const ct = videoEl?.currentTime;
    const dur = videoEl?.duration || 0;
    setCurrentTime(ct);
    if (dur > 0) {
      const pct = Math.round((ct / dur) * 100);
      setWatchPercent(pct);

      [25, 50, 75]?.forEach(milestone => {
        if (pct >= milestone && !firedMilestonesRef?.current?.has(milestone)) {
          firedMilestonesRef?.current?.add(milestone);
          if (typeof onWatchEvent === 'function') {
            onWatchEvent({
              milestone: `${milestone}%`,
              watchPercentage: milestone,
              watchDurationSeconds: ct,
              totalDurationSeconds: dur,
              completed: false,
              replayCount: replayCountRef?.current,
            });
          }
        }
      });
    }
  };

  const handleDurationChange = () => {
    const videoEl = videoRef?.current;
    if (videoEl) setDuration(videoEl?.duration || 0);
  };

  const handleVideoEnded = () => {
    setPlaying(false);
    setWatchPercent(100);
    const videoEl = videoRef?.current;
    const dur = videoEl?.duration || 0;
    if (!firedMilestonesRef?.current?.has(100)) {
      firedMilestonesRef?.current?.add(100);
    }
    if (typeof onWatchEvent === 'function') {
      onWatchEvent({
        milestone: '100%',
        watchPercentage: 100,
        watchDurationSeconds: dur,
        totalDurationSeconds: dur,
        completed: true,
        replayCount: replayCountRef?.current,
      });
    }
    if (typeof onVideoComplete === 'function') onVideoComplete();
  };

  const thumbnail = propThumbnail || playbackData?.thumbnailUrl;
  const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;

  if (loading) {
    return (
      <div className={`flex items-center justify-center bg-gray-900 rounded-lg aspect-video ${className}`}>
        <div className="flex flex-col items-center gap-2">
          <Loader className="w-8 h-8 text-white animate-spin" />
          <p className="text-white text-sm">Loading video...</p>
        </div>
      </div>
    );
  }

  if (error && !playing) {
    return (
      <div className={`flex items-center justify-center bg-gray-900 rounded-lg aspect-video ${className}`}>
        <div className="flex flex-col items-center gap-2 p-4 text-center">
          <AlertCircle className="w-8 h-8 text-red-400" />
          <p className="text-white text-sm">{error}</p>
          <button
            onClick={loadPlaybackUrl}
            className="mt-2 text-xs text-indigo-300 hover:text-indigo-100 underline"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`${className}`}>
      {/* Video container */}
      <div
        ref={containerRef}
        className="relative bg-black rounded-lg overflow-hidden aspect-video"
        onClick={() => setShowQualityMenu(false)}
      >
        {/* Video element */}
        <video
          ref={videoRef}
          className={`w-full h-full object-contain ${playing ? 'block' : 'hidden'}`}
          playsInline
          onEnded={handleVideoEnded}
          onWaiting={() => setBuffering(true)}
          onPlaying={() => setBuffering(false)}
          onCanPlay={() => setBuffering(false)}
          onTimeUpdate={handleTimeUpdate}
          onDurationChange={handleDurationChange}
          onLoadedMetadata={handleDurationChange}
        />

        {/* Caption overlay */}
        {playing && captionsEnabled && activeCaptionSeg && (
          <div className="absolute bottom-16 left-0 right-0 flex justify-center px-4 pointer-events-none z-25">
            <div className={`max-w-2xl text-center px-3 py-1.5 rounded-lg text-sm font-medium leading-snug shadow-lg ${
              activeCaptionSeg?.is_key_point
                ? 'bg-yellow-400/90 text-gray-900' :'bg-black/75 text-white'
            }`}>
              {activeCaptionSeg?.text}
              {activeCaptionSeg?.is_key_point && activeCaptionSeg?.teaching_point && (
                <div className="text-xs font-bold mt-0.5 text-yellow-900">
                  ★ {activeCaptionSeg?.teaching_point}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Buffering overlay */}
        {playing && buffering && (
          <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 pointer-events-none z-20">
            <div className="flex flex-col items-center gap-2">
              <Loader className="w-10 h-10 text-white animate-spin" />
              <p className="text-white text-xs">Buffering...</p>
            </div>
          </div>
        )}

        {/* Fatal error overlay (while playing) */}
        {playing && fatalError && (
          <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-70 z-20">
            <div className="flex flex-col items-center gap-3 p-4 text-center">
              <AlertCircle className="w-8 h-8 text-red-400" />
              <p className="text-white text-sm">{error}</p>
              <button
                onClick={(e) => { e?.stopPropagation(); handleManualRetry(); }}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-sm rounded-lg transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Retry
              </button>
            </div>
          </div>
        )}

        {/* Bottom controls bar */}
        {playing && (
          <div className="absolute bottom-0 left-0 right-0 z-30 bg-gradient-to-t from-black/80 to-transparent px-3 pb-2 pt-6">
            {/* Seekable timeline */}
            <div
              ref={progressBarRef}
              className="relative w-full h-2 bg-white/30 rounded-full cursor-pointer mb-2 group"
              onMouseDown={handleProgressMouseDown}
              onTouchStart={handleProgressTouchStart}
              onClick={(e) => { e?.stopPropagation(); scrubToPosition(e?.clientX); }}
            >
              <div
                className="absolute left-0 top-0 h-full bg-indigo-400 rounded-full transition-none"
                style={{ width: `${progressPercent}%` }}
              />
              <div
                className="absolute top-1/2 -translate-y-1/2 w-3.5 h-3.5 bg-white rounded-full shadow-md opacity-0 group-hover:opacity-100 transition-opacity"
                style={{ left: `calc(${progressPercent}% - 7px)` }}
              />
            </div>

            {/* Timestamps + controls */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-white text-xs tabular-nums">
                  {formatTime(currentTime)} / {formatTime(duration)}
                </span>
                <span className="text-white/70 text-xs tabular-nums">
                  {watchPercent}% watched
                </span>
              </div>

              <div className="flex items-center gap-2">
                {/* Captions toggle — only show if captions exist */}
                {captions?.length > 0 && (
                  <button
                    onClick={(e) => { e?.stopPropagation(); setCaptionsEnabled(prev => !prev); }}
                    className={`flex items-center gap-1 px-2 py-1 bg-black bg-opacity-60 hover:bg-opacity-80 text-white text-xs rounded transition-colors ${
                      captionsEnabled
                        ? 'bg-indigo-600 text-white' :'bg-black bg-opacity-60 hover:bg-opacity-80 text-white/60'
                    }`}
                    title={captionsEnabled ? 'Hide captions' : 'Show captions'}
                  >
                    {captionsEnabled ? <Captions className="w-4 h-4" /> : <CaptionsOff className="w-4 h-4" />}
                  </button>
                )}

                {/* Quality selector */}
                {qualityLevels?.length > 0 && (
                  <div className="relative">
                    <button
                      onClick={(e) => { e?.stopPropagation(); setShowQualityMenu(prev => !prev); }}
                      className="flex items-center gap-1 px-2 py-1 bg-black bg-opacity-60 hover:bg-opacity-80 text-white text-xs rounded transition-colors"
                      title="Quality"
                    >
                      <Settings className="w-3.5 h-3.5" />
                      <span>{currentLevel === -1 ? 'Auto' : qualityLevels?.find(l => l?.index === currentLevel)?.label || 'Auto'}</span>
                    </button>
                    {showQualityMenu && (
                      <div
                        className="absolute bottom-full right-0 mb-1 bg-gray-900 border border-gray-700 rounded-lg overflow-hidden shadow-xl min-w-[90px]"
                        onClick={(e) => e?.stopPropagation()}
                      >
                        <button
                          onClick={() => handleQualityChange(-1)}
                          className={`w-full text-left px-3 py-1.5 text-xs hover:bg-gray-700 transition-colors ${
                            currentLevel === -1 ? 'text-indigo-400 font-semibold' : 'text-white'
                          }`}
                        >
                          Auto
                        </button>
                        {qualityLevels?.map((lvl) => (
                          <button
                            key={lvl?.index}
                            onClick={() => handleQualityChange(lvl?.index)}
                            className={`w-full text-left px-3 py-1.5 text-xs hover:bg-gray-700 transition-colors ${
                              currentLevel === lvl?.index ? 'text-indigo-400 font-semibold' : 'text-white'
                            }`}
                          >
                            {lvl?.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Fullscreen button */}
                <button
                  onClick={(e) => { e?.stopPropagation(); toggleFullscreen(); }}
                  className="flex items-center gap-1 px-2 py-1 bg-black bg-opacity-60 hover:bg-opacity-80 text-white text-xs rounded transition-colors"
                  title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
                >
                  {isFullscreen
                    ? <Minimize className="w-4 h-4" />
                    : <Maximize className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Thumbnail + Play overlay */}
        {!playing && (
          <div
            className="absolute inset-0 flex items-center justify-center cursor-pointer group"
            onClick={handlePlay}
          >
            {thumbnail ? (
              <img
                src={thumbnail}
                alt={title || 'Video thumbnail'}
                className="absolute inset-0 w-full h-full object-cover"
              />
            ) : (
              <div className="absolute inset-0 bg-gray-800 flex items-center justify-center">
                <Film className="w-12 h-12 text-gray-600" />
              </div>
            )}
            <div className="absolute inset-0 bg-black bg-opacity-30 group-hover:bg-opacity-50 transition-all" />
            <div className="relative z-10 w-16 h-16 bg-white bg-opacity-90 group-hover:bg-opacity-100 rounded-full flex items-center justify-center shadow-lg transition-all group-hover:scale-110">
              <Play className="w-7 h-7 text-indigo-700 ml-1" fill="currentColor" />
            </div>
            {title && (
              <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black to-transparent">
                <p className="text-white text-sm font-medium truncate">{title}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Teaching Points Panel */}
      {keyPoints?.length > 0 && (
        <div className="mt-2 border border-purple-200 rounded-lg overflow-hidden">
          <button
            onClick={() => setShowTeachingPanel(prev => !prev)}
            className="w-full flex items-center justify-between px-4 py-2.5 bg-purple-50 hover:bg-purple-100 transition-colors text-sm font-medium text-purple-800"
          >
            <div className="flex items-center gap-2">
              <BookOpen className="w-4 h-4" />
              <span>Key Teaching Points ({keyPoints?.length})</span>
            </div>
            {showTeachingPanel ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>

          {showTeachingPanel && (
            <div className="divide-y divide-purple-100 bg-white">
              {keyPoints?.map((kp, idx) => (
                <button
                  key={idx}
                  onClick={() => seekToTimestamp(kp?.timestamp_seconds)}
                  className="w-full flex items-start gap-3 px-4 py-3 hover:bg-purple-50 transition-colors text-left group"
                >
                  <span className="flex-shrink-0 mt-0.5 text-xs font-mono bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded group-hover:bg-purple-200 transition-colors">
                    {formatTime(kp?.timestamp_seconds)}
                  </span>
                  <div className="flex-1 min-w-0">
                    {kp?.teaching_point && (
                      <p className="text-sm font-semibold text-purple-900 leading-snug">
                        ★ {kp?.teaching_point}
                      </p>
                    )}
                    <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{kp?.text}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MuxVideoPlayer;
